import {
  PeopleTableInfo,
  Person,
  PersonCompanyGeneralInfo,
  PersonContactDetailsItem,
} from '@aibees/elephant/feature/search-table-people/types';
import { ROUTES as R } from '@aibees/elephant/router';
import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { EmploymentType, RoleDisplayLabel } from '@aibees/elephant/utils';
import { Nullable } from '@aibees/shared/definition';
import { fDate, fToNow } from '@aibees/shared/utils';
import { ContactDetailsSliderTabTitle } from './types';

export function doesContactDetailHaveUpdate(
  item: PersonContactDetailsItem,
  data?: PeopleTableInfo
) {
  return !!data?.changes?.CONTACT?.includes(item);
}

// On a not-yet-unlocked profile, only an EXPERIENCE change is actionable — a
// bell on any other tab would promise something the user cannot trigger.
//
// TODO: this guard may now be obsolete. Its original reason was that Update
// sent CONTACT_UPDATE_JOB_CHANGE, which only refreshes EXPERIENCE; that is no
// longer how the action is chosen. Meanwhile the backend has started gating
// changes.CONTACT the same way server-side, reporting only EXPERIENCE while the
// profile is locked — which would make this a no-op. Kept until that gating is
// confirmed on a live record; if it holds, this collapses into
// doesContactDetailHaveUpdate and both callers can use that directly.
export function doesContactDetailHaveActionableUpdate(
  item: PersonContactDetailsItem,
  data?: PeopleTableInfo
) {
  if (!doesContactDetailHaveUpdate(item, data)) return false;
  const profileUnlocked = !!data?.revelation?.contact?.state;
  if (profileUnlocked) return true;
  return item === PersonContactDetailsItem.EXPERIENCE;
}

export function doesContactDetailHaveAnyUpdates(data?: PeopleTableInfo) {
  return !!data?.changes?.CONTACT?.length;
}

export function profileDetailsLatestUpdateDate(data?: PeopleTableInfo) {
  return fToNow(data?.revelation?.contact?.created);
}

export function getContactDetailsCompanyLink(
  item?: Nullable<PersonCompanyGeneralInfo>
) {
  const id = item?.id;
  return id && R.company.replace(':id', id);
}

export function formattedContactDetailsDate(date?: Nullable<string>) {
  return fDate(date, 'yyyy');
}

export function formattedStartToEndDuration(
  start?: Nullable<string>,
  end?: Nullable<string>
) {
  return `${formattedContactDetailsDate(start)} - ${
    !end ? 'Present' : formattedContactDetailsDate(end)
  }`;
}

export function contactDetailsAvailableItems(data?: PeopleTableInfo) {
  return data?.revelation?.contact?.content ?? data?.available ?? [];
}

export function contactDetailsUnlockableItems(data?: PeopleTableInfo) {
  const available = contactDetailsAvailableItems(data);
  // Follows the experience change, not the company change: a same-company title
  // change must keep EXPERIENCE in the list. Every consumer gates on `.length`,
  // so dropping it would empty the array and hide the Update row and its button
  // altogether, leaving no way to run the update.
  const hasExperienceChange = isExperienceChangeHappened(data);
  return available.filter(
    (i) =>
      (i !== PersonContactDetailsItem.EXPERIENCE || hasExperienceChange) &&
      i !== PersonContactDetailsItem.LANGUAGES
  );
}

export function isRefetchNeededAfterRevealContactDetails(data?: Person) {
  let response = false;
  if (data?.revelation?.contact?.state === ENRICHMENT_STATE.SUCCESS) {
    data.revelation.contact.content?.map((item) => {
      switch (item) {
        case PersonContactDetailsItem.EXPERIENCE:
          if (!data?.positionGroups) response = true;
          break;
        case PersonContactDetailsItem.PUBLICATIONS:
          if (!data?.publications?.length) response = true;
          break;
        case PersonContactDetailsItem.VOLUNTEER_EXPERIENCES:
          if (!data?.volunteerExperiences?.length) response = true;
          break;
        case PersonContactDetailsItem.CERTIFICATIONS:
          if (!data?.certifications?.length) response = true;
          break;
        case PersonContactDetailsItem.PROJECTS:
          if (!data?.projects?.length) response = true;
          break;
        case PersonContactDetailsItem.EDUCATIONS:
          if (!data?.educations?.length) response = true;
          break;
        // TODO: this case will change when-ever the back-end API is ready
        // case PersonContactDetailsItem.RECOMMENDATION:
        //   return false;
        case PersonContactDetailsItem.LANGUAGES:
          if (!data?.languages?.profile_languages?.length) response = true;
          break;
        default:
          break;
      }
    });
  }
  return response;
}

// "Job change" means the Top Current Company changed, and nothing else. The
// backend signals it with its own `COMPANY` key — a single-member array, read
// by length like `changes.EMAIL`.
export function isJobChangeHappened(data?: PeopleTableInfo) {
  return !!data?.changes?.COMPANY?.length;
}

// Any movement in the work history, a company change included: same-company
// title, date, employment-type and secondary-position edits all land here. The
// backend guarantees a company change always sets CONTACT ∋ EXPERIENCE as well,
// so this is the predicate for "the work history moved at all" — no combined
// `isJobChangeHappened || isExperienceChangeHappened` is ever needed.
export function isExperienceChangeHappened(data?: PeopleTableInfo) {
  return !!data?.changes?.CONTACT?.includes(
    PersonContactDetailsItem.EXPERIENCE
  );
}

function formatCurrentCompanyList(companies: string[]) {
  if (companies.length === 1) return companies[0];
  if (companies.length === 2) return `${companies[0]} and ${companies[1]}`;
  return `${companies[0]}, ${companies[1]}, and ${companies.length - 2} more`;
}

function formatEmploymentClause(type: NonNullable<EmploymentType>) {
  return type === 'Freelancer' ? 'a Freelancer' : 'Self-Employed';
}

export function getJobChangeAlertMessage(params: {
  firstName: Nullable<string> | undefined;
  currentLabels: RoleDisplayLabel[];
  previousLabel: RoleDisplayLabel | null;
}) {
  const name = params.firstName?.trim() || 'This person';
  const { currentLabels, previousLabel } = params;

  const currentCompanies = currentLabels
    .filter(
      (l): l is Extract<RoleDisplayLabel, { kind: 'company' }> =>
        l.kind === 'company'
    )
    .map((l) => l.name);

  if (currentCompanies.length > 0) {
    const list = formatCurrentCompanyList(currentCompanies);
    return `${name}'s work experience has been updated — currently listed at ${list}.`;
  }

  const currentEmployment = currentLabels.find(
    (l): l is Extract<RoleDisplayLabel, { kind: 'employment' }> =>
      l.kind === 'employment'
  );

  if (currentEmployment) {
    return `${name}'s work experience has been updated — currently listed as ${formatEmploymentClause(
      currentEmployment.type
    )}.`;
  }

  if (previousLabel?.kind === 'company') {
    return `${name} is no longer listed at ${previousLabel.name}.`;
  }

  if (previousLabel?.kind === 'employment') {
    return `${name} is no longer listed as ${formatEmploymentClause(
      previousLabel.type
    )}.`;
  }

  return `${name}'s work experience has been updated.`;
}

export function showProfileInsightItemTabBadge(
  locked: boolean,
  key: ContactDetailsSliderTabTitle,
  hasExperienceChange: boolean,
  loading: boolean,
  hasUpdate: boolean
) {
  return (
    locked ||
    (key === ContactDetailsSliderTabTitle.Work_History &&
      hasExperienceChange) ||
    loading ||
    hasUpdate
  );
}
